// Weekend-gap name flow, shared by GanttChart.js and TemplateGantt.js (like
// ZoomSlider). Assigns WHOLE words to a bar's working segments - a name is
// never cut at a weekend gap (Bill, 2026-08-21); a single › chevron marks each
// side of a split. Plan: Planning/GANTT-NAME-FLOW-2026-08-21.md.

// pl-3 on a segment that starts the name; a chevron's own width is measured,
// this is the pl-1/pr-1 air around it.
const PAD_LEFT = 12;
const CHEVRON_PAD = 8;

// Canvas-backed text measurer for one font string, word widths cached.
// jsdom has no 2d context - callers get a constant-0 measurer there, and the
// component tests stub getContext instead (see the plan's test section).
export function makeTextMeasurer(fontString) {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas ? canvas.getContext('2d') : null;
    if (!ctx) return () => 0;
    ctx.font = fontString;
    const cache = new Map();
    return (text) => {
        let w = cache.get(text);
        if (w === undefined) {
            w = ctx.measureText(text).width;
            cache.set(text, w);
        }
        return w;
    };
}

// Pure layout. segmentWidths are the WORKING segments' pixel widths, in order
// (weekend gaps excluded). Returns one entry per segment:
//   { text, startChevron, endChevron, truncate }
// - Greedy fill with whole words only.
// - A word wider than the current segment's room moves whole to the first
//   later segment that holds it; skipped segments carry chevrons only.
// - A word no segment can hold is HYPHENATED across segments - as much as fits
//   plus '-', the rest carrying on (Bill's correction, 2026-08-21 evening:
//   "I can't have missing words" - the earlier widest-segment-with-ellipsis
//   rule parked the word on one segment, where flex squeezed it invisible).
//   truncate: true survives only for the degenerate tail that reaches the last
//   segment still too long.
// - Words left over when segments run out append to the last segment, which
//   the markup always truncates.
export function flowWords(text, segmentWidths, measure) {
    const flows = segmentWidths.map(() => ({ text: '', startChevron: false, endChevron: false, truncate: false }));
    const words = String(text || '').split(/\s+/).filter(Boolean);
    if (!words.length || !segmentWidths.length) return flows;

    const chevronW = measure('›') + CHEVRON_PAD;
    const spaceW = measure(' ');
    const last = segmentWidths.length - 1;
    // Room for words: lead is the name padding (first segment) or the incoming
    // chevron (continuations); every non-last segment reserves the outgoing
    // chevron - unused reservation when the name ends there is accepted.
    const availOf = (s) => segmentWidths[s] - (s > 0 ? chevronW : PAD_LEFT) - (s < last ? chevronW : 0);

    let s = 0;
    let i = 0;
    while (i < words.length && s <= last) {
        const flow = flows[s];
        const used = flow.text ? measure(flow.text) : 0;
        const wordW = measure(words[i]);
        const need = flow.text ? spaceW + wordW : wordW;

        if (used + need <= availOf(s)) {
            flow.text = flow.text ? `${flow.text} ${words[i]}` : words[i];
            i++;
            continue;
        }

        // The word does not fit this segment's remaining room - hyphenate from
        // RIGHT HERE, using the leftover room, so the name reads continuously
        // and no leading segment sits empty (Bill, 2026-08-21 evening: the
        // whole-word-to-next-segment preference "looks odd and reduces overall
        // text space", and its any-later-segment ancestor crammed words
        // invisibly off the bar's end).
        if (s === last) { s++; continue; } // leftover append below

        // Hyphenate across segments from here.
        let rest = words[i];
        i++;
        while (rest && s <= last) {
            const flow = flows[s];
            const used = flow.text ? measure(flow.text) + spaceW : 0;
            const room = availOf(s) - used;
            if (measure(rest) <= room) {
                flow.text = flow.text ? `${flow.text} ${rest}` : rest;
                rest = '';
                break;
            }
            // A hyphen head on the LAST segment has nowhere to send its tail -
            // the whole remainder lands there and the CSS ellipsis cuts it.
            if (s === last) break;
            // Longest head that fits with its hyphen; never split under 2 chars.
            let cut = 0;
            for (let c = rest.length - 1; c >= 2; c--) {
                if (measure(`${rest.slice(0, c)}-`) <= room) { cut = c; break; }
            }
            if (cut === 0) {
                s++;
                continue;
            }
            flow.text = flow.text ? `${flow.text} ${rest.slice(0, cut)}-` : `${rest.slice(0, cut)}-`;
            rest = rest.slice(cut);
            s++;
        }
        if (rest) {
            const flow = flows[last];
            flow.text = flow.text
                ? (flow.text.endsWith('-') ? `${flow.text}${rest}` : `${flow.text} ${rest}`)
                : rest;
            flow.truncate = true;
            s = last;
        }
    }
    if (i < words.length) {
        const rest = words.slice(i).join(' ');
        const flow = flows[last];
        // No space after a hyphenated head - the tail continues the word.
        flow.text = flow.text
            ? (flow.text.endsWith('-') ? `${flow.text}${rest}` : `${flow.text} ${rest}`)
            : rest;
        flow.truncate = true;
    }

    // Chevrons from where the name actually reaches: every boundary crossed
    // gets one on each side, pass-through (empty) segments get both.
    let lastPlaced = 0;
    for (let t = last; t >= 0; t--) {
        if (flows[t].text) { lastPlaced = t; break; }
    }
    flows.forEach((flow, idx) => {
        flow.startChevron = idx > 0 && idx <= lastPlaced;
        flow.endChevron = idx < lastPlaced;
    });
    return flows;
}
